import '@css/Button.css'

import { getRouteToGetBackFromGraph } from '@constants/utils'
import { useYisNavigate } from '@hooks/useYisNavigate'
import { Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { styled } from '@mui/material/styles'
import type { CSSProperties, JSX, ReactNode } from 'react'
import React from 'react'
import { useTranslation } from 'react-i18next'

import BackButton from '../material/YisBackButton'

const Container = styled(Grid)({
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  padding: '5vh 8vw',
  '@media (max-width: 1179px)': {
    padding: '3vh 8vw'
  }
})

const Title = styled(Typography, { shouldForwardProp: prop => prop !== 'staticPos' })<{ staticPos?: boolean }>(({ staticPos }) => ({
  padding: 0,
  textTransform: 'capitalize',
  ...(staticPos ? { marginLeft: '2vw' } : {})
}))

const RightWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center'
})

const ContentColumn = styled(Grid)({
  flexDirection: 'column',
  flexWrap: 'nowrap',
  width: '100%',
  height: '100%',
  minHeight: 0
})

const HeaderWrapper = styled(Grid)({
  width: '100%'
})

const HeaderContent = styled(Grid)({
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%'
})

const ContentArea = styled(Grid)({
  flex: '1 1 auto',
  minHeight: 0,
  width: '100%',
  '& > .MuiGrid-root': {
    width: '100%'
  }
})

interface MainWrapperProps {
  title?: string
  subtitle?: string | ReactNode
  className?: string
  onClose?: () => void
  absolute?: boolean
  showBackBtn?: boolean
  containerClass?: string
  pushToRoute?: string
  RightSide?: ReactNode | null
  btnLeft?: string | number
  staticPos?: boolean
  children?: JSX.Element | JSX.Element[]
  withPushBack?: boolean
}

export default function MainWrapper (props: MainWrapperProps) {
  const { t } = useTranslation()
  const navigate = useYisNavigate()

  const {
    title = '',
    className = '',
    containerClass = '',
    onClose,
    children,
    RightSide,
    staticPos
  } = props

  const pushToRoute = props.withPushBack ? getRouteToGetBackFromGraph() : props.pushToRoute

  const showBackBtn = props.showBackBtn || pushToRoute

  const handleClose = () => {
    if (onClose) {
      onClose()
    } else if (pushToRoute) {
      navigate(pushToRoute)
    } else {
      navigate(-1)
    }
  }

  const style: CSSProperties = props.absolute ? { position: 'absolute', top: '8vh' } : {}

  const backCntStyle: CSSProperties = { height: '5rem', ...style }
  const backCntClasses = 'display align-center-middle container-start float-left'

  return (
    <Container container className={className}>
      <ContentColumn container>
        {showBackBtn && (
          <HeaderWrapper
            container
            direction="row"
            style={backCntStyle}
            className={backCntClasses}
          >
            <HeaderContent container>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <BackButton onClick={handleClose} />

                {title && <Title staticPos={staticPos} variant="h5">{t(title)}</Title>}
              </div>

              <RightWrapper>
                {RightSide}
              </RightWrapper>
            </HeaderContent>
          </HeaderWrapper>
        )}

        <ContentArea container className={containerClass}>
          {children}
        </ContentArea>
      </ContentColumn>
    </Container>
  )
}
